import type { RawRuleOf } from '@casl/ability';

import type {
    Ability,
    ConfirmDeletionInput,
    DeletionVerificationRequestResult,
    User,
    UserPreference,
    Workspace,
} from '@/types';

import { apiClient } from '../http';

export type AuthenticatedUserMetadata = {
    accountTFAEnabled: boolean;
    workspaceTFAEnabled: boolean;
};

export type AuthenticatedUserResponse = {
    data: User | null;
    metadata: AuthenticatedUserMetadata | null;
    session: Record<string, unknown> | null;
};

export type WorkspaceResourceMetadata = {
    dataSources?: number;
    dashboards?: number;
    folders?: number;
    investments?: number;
    lists?: number;
    members?: number;
    memos?: number;
    pipelines?: number;
    tags?: number;
    teams?: number;
    watchlists?: number;
};

export type AuthenticatedUserPermissionsResponse = {
    rules: RawRuleOf<Ability>[];
};

export type CompanyDomainSearchResult = {
    domain: string;
    logo_url: string;
    name: string;
};

export type UserPreferenceScope = 'account' | 'workspace';

export const userService = {
    deleteAuthenticated: (input: ConfirmDeletionInput) => apiClient.delete<unknown>('/api/me', { data: input }),
    getAuthenticated: () => apiClient.get<AuthenticatedUserResponse>('/api/me', { suppressErrorCapture: true }),
    getMetadata: () => apiClient.get<WorkspaceResourceMetadata>('/api/me/metadata'),
    getPermissions: () => apiClient.get<AuthenticatedUserPermissionsResponse>('/api/me/permissions'),
    getPreferences: (scope: UserPreferenceScope = 'workspace') =>
        apiClient.get<UserPreference, { scope: UserPreferenceScope }>('/api/me/preferences', { params: { scope } }),
    searchCompanyDomain: (name: string) =>
        apiClient.get<CompanyDomainSearchResult[], { name: string }>('/api/me/search/companies', { params: { name } }),
    resendVerificationEmail: (id: string) =>
        apiClient.put<void, { id: string }>('/api/services/mail/verify/resend', { id }),
    requestDeletionVerification: () =>
        apiClient.post<DeletionVerificationRequestResult>('/api/me/deletion-verification'),
    updateAuthenticated: (updates: Partial<User>) => apiClient.put<User, Partial<User>>('/api/me', updates),
    updateActiveWorkspace: (userId: string, workspaceId: string) =>
        apiClient.put<Workspace, { id: string }>(`/api/users/${userId}/activeWorkspace`, { id: workspaceId }),
    updatePreferences: (updates: Partial<UserPreference['preferences']>, scope: UserPreferenceScope = 'workspace') =>
        apiClient.patch<UserPreference, Partial<UserPreference['preferences']>, { scope: UserPreferenceScope }>(
            '/api/me/preferences',
            updates,
            { params: { scope } },
        ),
};
